import { MALEK_APP_PROMO_KEY } from "src/constants/storageKeys";
import { APP_PROMO_SNOOZE_DAYS } from "src/constants/vars";
import { getStorageItem, removeStorageItem, saveStorageItem } from "./storage";

/** What the visitor picked the last time the modal was shown. */
export type AppPromoChoice = "android" | "ios" | "web";

export interface AppPromoRecord {
    choice: AppPromoChoice;
    /** Epoch ms. The modal stays hidden until this moment passes. */
    snoozed_until: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const isChoice = (value: unknown): value is AppPromoChoice => (
    value === "android" || value === "ios" || value === "web"
);

/**
 * Reads the stored decision, treating anything unparseable as "never asked".
 * A corrupt or hand-edited entry should re-offer the modal, not wedge it shut.
 */
export const getAppPromoRecord = (): AppPromoRecord | null => {
    const stored = getStorageItem(MALEK_APP_PROMO_KEY);
    if (!stored || typeof stored !== "object") return null;

    const { choice, snoozed_until: snoozedUntil } = stored as Record<string, unknown>;
    if (!isChoice(choice)) return null;
    if (typeof snoozedUntil !== "number" || !Number.isFinite(snoozedUntil)) return null;

    return { choice, snoozed_until: snoozedUntil };
};

/** True once no valid decision covers the current moment. */
export const shouldShowAppPromo = (now: number = Date.now()): boolean => {
    const record = getAppPromoRecord();
    if (!record) return true;
    return now >= record.snoozed_until;
};

export const rememberAppPromoChoice = (
    choice: AppPromoChoice,
    now: number = Date.now(),
    days: number = APP_PROMO_SNOOZE_DAYS,
): void => {
    saveStorageItem(MALEK_APP_PROMO_KEY, {
        choice,
        snoozed_until: now + days * DAY_MS,
    } satisfies AppPromoRecord);
};

export const clearAppPromoChoice = (): void => {
    removeStorageItem(MALEK_APP_PROMO_KEY);
};
